import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { DocumentFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DocumentDraftTodoFormContainerProps } from "models/propTypes/documentDraftPropTypes";
import { CommonEnums } from "models/enums";
//#endregion
//#region components
import { useDocumentDraftService, useFileUploadService } from "serviceHooks";
import DocumentCreateForm from "../../forms/createForm";
import useCreateFormHelpers from "../../forms/useCreateFormHelpers";
//#endregion
import { FileHelper } from "utils/helpers";

const DocumentDraftTodoSelectionFormContainer: ForwardRefRenderFunction<
  FormHandles,
  DocumentDraftTodoFormContainerProps
> = (props, ref) => {
  const fileUploadService = useFileUploadService();
  const documentDraftService = useDocumentDraftService();
  const { documentDraft, ...formProps } = props;

  const {
    initialValues,
    validationSchema,
    onReceiptCategorySelect,
    onReceiptCategoryClear,
    onAmountChange,
    onCurrencyChange,
    availableDocumentTypes,
    receiptCategoriesInputItems,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptPaymentMethodActions,
    dimensionAmount,
    currency,
    isReceiptLoading,
    setIsReceiptLoading,
  } = useCreateFormHelpers(
    true,
    true,
    documentDraft.created,
    documentDraft.attachments?.map((attachment) => ({
      ...attachment,
      fileName: attachment.fileName ?? FileHelper.getEmptyFileName(attachment.contentType ?? ""),
      fileSize: attachment.data?.length ?? 0,
      data: attachment.data ?? "",
      contentType: attachment.contentType ?? "",
    }))
  );

  const handleSubmit = async (values: DocumentFormTypes.CreateNew) => {
    if (values.type !== undefined) {
      const documentDraftPost: WintSuperkollAPIModel.DocumentDraftPost = {
        id: documentDraft.id,
        type: documentDraftService.documentTypeToDocumentDraftType(values.type),
      };

      const files = await fileUploadService.updateMissingAttachments(
        values.filesToUpload ?? [],
        documentDraftService.getAttachmentData.bind(null, documentDraft.id)
      );

      const updatedValues: DocumentFormTypes.CreateNew = {
        ...values,
        filesToUpload: files,
      };

      switch (values.type) {
        case CommonEnums.DocumentTypes.Receipt:
          documentDraftPost.receipt = documentDraftService.formToReceiptPost(updatedValues);
          break;
        case CommonEnums.DocumentTypes.IncomingInvoice:
          documentDraftPost.incomingInvoice = documentDraftService.formToIncomingInvoicePost(updatedValues);
          break;
        case CommonEnums.DocumentTypes.GenericDocument:
          documentDraftPost.genericDocument = documentDraftService.formToGenericDocumentPost(updatedValues);
          break;
      }

      return await documentDraftService.post(documentDraftPost);
    }
    throw new Error("Type not selected");
  };

  return (
    <DocumentCreateForm
      {...formProps}
      ref={ref}
      receiptCategorySelectHandler={onReceiptCategorySelect}
      receiptCategoryClearHandler={onReceiptCategoryClear}
      amountChangeHandler={onAmountChange}
      currencyChangeHandler={onCurrencyChange}
      availableDocumentTypes={availableDocumentTypes}
      receiptCategories={receiptCategoriesInputItems}
      receiptPaymentMethods={receiptPaymentMethods}
      topReceiptPaymentMethods={topReceiptPaymentMethods}
      receiptPaymentMethodActions={receiptPaymentMethodActions}
      dimensionAmount={dimensionAmount}
      currency={currency}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      onFormValidationChange={props.onFormValidationChange}
      isReceiptLoading={isReceiptLoading}
      setIsReceiptLoading={setIsReceiptLoading}
      isDocumentDraft
    />
  );
};

export default forwardRef(DocumentDraftTodoSelectionFormContainer);
